import { Component, OnInit, AfterViewInit, OnDestroy, inject, signal, PLATFORM_ID } from '@angular/core';
import { NgClass, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { ScrollDirectionDirective } from '../../directives/scroll-direction.directive';
import { NavigationService } from '../../Services/navigation.service';
import { SmoothScrollOptions } from '../../Services/smooth-scroll.service';

@Component({
  selector: 'app-header',
  imports: [
    NgClass,
    ScrollDirectionDirective
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header implements OnInit, AfterViewInit, OnDestroy {
  // Injected services and dependencies
  navigationService = inject(NavigationService);
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser: boolean = isPlatformBrowser(this.platformId);
  private readonly doc: Document = inject(DOCUMENT);

  isMobileWidth = false;
  isHeaderMove = false;
  menuOpen = signal(false);

  // Event handler references for cleanup
  private scrollHandler?: () => void;
  private resizeHandler?: () => void;

  // Header navigation items with smooth scroll configuration
  headerItems: Array<{ title: string; link: string; scrollOptions?: SmoothScrollOptions }> = [
    { title: 'About Us', link: '#about-us', scrollOptions: { duration: 700, offset: 80, block: 'start' } },
    { title: 'Services', link: '#our-services', scrollOptions: { duration: 700, offset: 80, block: 'start' } },
    { title: 'Projects', link: '#projects', scrollOptions: { duration: 700, offset: 80, block: 'start' } },
    { title: 'Testimonials', link: '#testimonials', scrollOptions: { duration: 700, offset: 80, block: 'start' } },
  ];

  private getScrollTop(): number {
    if (!this.isBrowser) return 0;
    return (
      window.pageYOffset ||
      this.doc.documentElement?.scrollTop ||
      this.doc.body?.scrollTop ||
      0
    );
  }

  private onWindowScroll(): void {
    if (!this.isBrowser) return;
    this.isHeaderMove = this.getScrollTop() > 180;
  }

  private onResize(): void {
    if (!this.isBrowser) return;
    this.isMobileWidth = window.innerWidth <= 991.98;
  }

  ngOnInit(): void {
    this.isMobileWidth = this.isBrowser ? window.innerWidth <= 991.98 : false;
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    this.isMobileWidth = window.innerWidth <= 991.98;

    // Defer event listener attachment to allow app to stabilize after hydration
    // This prevents blocking Angular stability check
    setTimeout(() => {
      this.isHeaderMove = this.getScrollTop() > 180;

      // Attach scroll and resize listeners after hydration completes
      this.scrollHandler = this.onWindowScroll.bind(this);
      this.resizeHandler = this.onResize.bind(this);

      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      window.addEventListener('resize', this.resizeHandler, { passive: true });
    }, 0);
  }


  toggleMenu(): void {
    this.menuOpen.update((v) => !v);
    if (!this.isBrowser) return;
    const body = this.doc.body;
    if (this.menuOpen()) body.classList.add('no-scroll');
    else body.classList.remove('no-scroll');
  }

  closeMenu(): void {
    this.menuOpen.set(false);
    if (!this.isBrowser) return;
    this.doc.body.classList.remove('no-scroll');
  }

  /**
   * Handles logo click with smooth scroll to hero section
   * Closes menu immediately for better UX and navigates smoothly
   */
  async onLogoClick(event: MouseEvent): Promise<void> {
    event.preventDefault();
    // Close menu immediately for instant feedback
    this.closeMenu();
    // Navigate to hero section with smooth scroll
    await this.navigationService.navigateTo('/', {
      elementId: 'hero',
      offset: 0,
      duration: 700,
      block: 'start',
    });
  }

  /**
   * Handles anchor link clicks with smooth scroll animation
   * Prevents default browser jump behavior and uses NavigationService for smooth scrolling
   */
  async onAnchorItemClick(event: MouseEvent, link: string, opts?: SmoothScrollOptions): Promise<void> {
    event.preventDefault();
    // Close mobile menu immediately for instant feedback
    this.closeMenu();
    // Extract element ID from link (remove '#' prefix)
    const id = link.startsWith('#') ? link.slice(1) : link;
    // Navigate to the element with smooth scroll animation
    await this.navigationService.navigateTo('/', {
      elementId: id,
      offset: opts?.offset ?? 80,
      duration: opts?.duration ?? 700,
      block: opts?.block ?? 'start',
      easing: opts?.easing,
    });
  }


  ngOnDestroy(): void {
    // Clean up event listeners to prevent memory leaks
    if (this.scrollHandler && this.isBrowser) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler && this.isBrowser) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
}
